import { EventBus } from '../utils/eventBus';
import { ApiService } from '../services/api';
import { clearElement } from '../utils/dom';

export class ModelSelector {
  private container: HTMLElement | null = null;
  private select: HTMLSelectElement | null = null;
  private models: string[] = [];
  private currentModel: string = 'gpt-4o-mini';
  private isLoading: boolean = false;

  constructor(
    private eventBus: EventBus,
    private apiService: ApiService
  ) {
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.eventBus.on('conversation:updated', (conversation) => {
      this.currentModel = conversation.model;
      this.updateSelectValue();
    });

    this.eventBus.on('activeConversation:changed', async () => {
      // Fetch models when active conversation changes
      if (this.models.length === 0) {
        await this.fetchModels();
      }
    });
  }

  mount(container: HTMLElement): void {
    this.container = container;
    this.render();
    this.fetchModels();
  }

  private render(): void {
    if (!this.container) return;

    clearElement(this.container);

    const label = document.createElement('label');
    label.className = 'model-selector-label';
    label.textContent = 'Model:';
    this.container.appendChild(label);

    this.select = document.createElement('select');
    this.select.className = 'model-dropdown';
    this.select.addEventListener('change', this.handleModelChange.bind(this));

    if (this.isLoading) {
      const option = document.createElement('option');
      option.textContent = 'Loading models...';
      this.select.appendChild(option);
      this.select.disabled = true;
    } else {
      this.renderOptions();
    }

    this.container.appendChild(this.select);
  }

  private renderOptions(): void {
    if (!this.select) return;

    clearElement(this.select);

    if (this.models.length === 0) {
      // Default models if fetch fails (all vision-capable)
      const defaultModels = ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'];
      defaultModels.forEach(model => {
        const option = document.createElement('option');
        option.value = model;
        option.textContent = model;
        this.select!.appendChild(option);
      });
    } else {
      this.models.forEach(model => {
        const option = document.createElement('option');
        option.value = model;
        option.textContent = model;
        this.select!.appendChild(option);
      });
    }

    this.updateSelectValue();
    this.select.disabled = false;
  }

  private updateSelectValue(): void {
    if (this.select) {
      this.select.value = this.currentModel;
    }
  }

  private async fetchModels(): Promise<void> {
    this.isLoading = true;
    this.render();

    try {
      this.models = await this.apiService.fetchModels();
      // Sort models by name
      this.models.sort();
    } catch (error) {
      console.error('Failed to fetch models:', error);
      this.models = [];
    }

    this.isLoading = false;
    this.render();
  }

  private handleModelChange(): void {
    if (!this.select) return;

    const newModel = this.select.value;
    this.currentModel = newModel;
    this.eventBus.emit('model:changed', newModel);
  }

  destroy(): void {
    this.eventBus.off('conversation:updated');
    this.eventBus.off('activeConversation:changed');
  }
}
